import React from "react"

import { Building2, Clock, Eye, MapPin, Pencil, User } from "lucide-react"

import { getShiftTypeIcon } from "@/components/sheriff/ui/shiftTypeIcons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ukFormat } from "@/lib/ukFormat"
import { type CancelledReason, type Shift } from "@/services/shiftsService"

// The row reads a block as it is displayed rather than as it is stored; a caller passes the Shift.
interface ShiftItem {
  id: string
  dateLabel: string
  startTime?: string
  endTime?: string
  realStartTime?: string
  realEndTime?: string
  area: string
  location: string
  cancelReason?: CancelledReason
  status: Shift["status"]
  // Only set where the blocks are not already grouped by person, as on a vehicle.
  personName?: string
}

function toShiftItem(shift: Shift, personName?: string): ShiftItem {
  const startDate = new Date(shift.startTime)
  const endDate = new Date(shift.endTime)

  return {
    id: shift.id,
    dateLabel: ukFormat(startDate, "EEE, d MMMM yyyy"),
    startTime: ukFormat(startDate, "HH:mm"),
    endTime: ukFormat(endDate, "HH:mm"),
    realStartTime: shift.timesheet?.clockedInTime
      ? ukFormat(shift.timesheet.clockedInTime, "HH:mm")
      : undefined,
    realEndTime: shift.timesheet?.clockedOutTime
      ? ukFormat(shift.timesheet.clockedOutTime, "HH:mm")
      : undefined,
    area: shift.area || "",
    location: shift.location,
    cancelReason: shift.cancelledReason as CancelledReason,
    status: shift.status,
    personName,
  }
}

const STATUS_COLORS: Record<Shift["status"], string> = {
  Open: "bg-blue-100 text-blue-700 border-blue-200",
  Filled: "bg-green-100 text-green-700 border-green-200",
  Cancelled: "bg-red-100 text-red-700 border-red-200",
  Unfulfilled: "bg-orange-100 text-orange-700 border-orange-200",
}

const SectionHeader: React.FC<{
  title: string
  count?: number
  tone: "upcoming" | "past"
}> = ({ title, count, tone }) => {
  const color =
    tone === "upcoming"
      ? "text-emerald-700 bg-emerald-50"
      : "text-palette-jungleGreen700 bg-palette-jungleGreen000"
  return (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-base font-semibold text-palette-jungleGreen900 flex items-center gap-2">
        {title}
        {count !== undefined && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${color}`}>{count}</span>
        )}
      </h3>
    </div>
  )
}

const ShiftRow: React.FC<{
  shift: ShiftItem
  variant: "upcoming" | "past"
  onEdit?: (id: string) => void
  onView?: (id: string) => void
  canEdit?: boolean
}> = ({ shift, variant, onEdit, onView, canEdit = true }) => {
  const areaLower = shift.area.toLowerCase()
  const formatDuration = (start?: string, end?: string) => {
    if (!start || !end) return ""
    const [sh, sm] = start.split(":").map(Number)
    const [eh, em] = end.split(":").map(Number)
    if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return ""
    const startMin = sh * 60 + sm
    let endMin = eh * 60 + em
    if (endMin < startMin) endMin += 24 * 60 // crosses midnight
    const diff = Math.max(0, endMin - startMin)
    const hours = Math.floor(diff / 60)
    const minutes = diff % 60
    if (hours > 0 && minutes > 0) return ` (${hours}h ${minutes}m)`
    if (hours > 0) return ` (${hours}h)`
    return ` (${minutes}m)`
  }
  const typeBadgeColor = (() => {
    if (areaLower.includes("rc1")) return "bg-emerald-50 text-emerald-700"
    if (areaLower.includes("rc2")) return "bg-amber-50 text-amber-700"
    if (areaLower.includes("collection")) return "bg-indigo-50 text-indigo-700"
    if (areaLower.includes("sortation")) return "bg-cyan-50 text-cyan-700"
    return "bg-palette-jungleGreen100 text-palette-jungleGreen700"
  })()

  const leftBorder = variant === "upcoming" ? "border-emerald-500" : "border-palette-jungleGreen400"
  const subtleBg = variant === "upcoming" ? "bg-emerald-50/40" : "bg-palette-jungleGreen000/60"

  const iconForType = () => {
    if (areaLower.includes("internal")) return <Building2 size={16} />
    const AreaIcon = getShiftTypeIcon(shift.area)
    return <AreaIcon size={16} />
  }

  return (
    <div
      className={`flex items-center justify-between rounded-md p-4 bg-white border shadow-sm ${subtleBg} border-l-4 ${leftBorder}`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`h-9 w-9 rounded-full flex items-center justify-center ${
            variant === "upcoming"
              ? "bg-emerald-100 text-emerald-700"
              : "bg-palette-jungleGreen100 text-palette-jungleGreen700"
          }`}
        >
          {iconForType()}
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-palette-jungleGreen900">
              {shift.dateLabel}
            </div>
            <Badge
              variant="secondary"
              className={`${typeBadgeColor} pointer-events-none select-none`}
            >
              {shift.area}
            </Badge>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-palette-jungleGreen700">
            {/* Status Badge */}
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium ${
                STATUS_COLORS[shift.status]
              }`}
            >
              {shift.status}
            </span>

            <span className="inline-flex items-center gap-1">
              <Clock size={14} />
              {variant === "upcoming" ? (
                <span>
                  {shift.startTime ?? "-"} - {shift.endTime ?? "-"}
                  {formatDuration(shift.startTime, shift.endTime)}
                </span>
              ) : shift.realStartTime && shift.realEndTime ? (
                <span>
                  {shift.realStartTime} - {shift.realEndTime}
                  {formatDuration(shift.realStartTime, shift.realEndTime)}
                </span>
              ) : (
                <span>N/A</span>
              )}
            </span>
            {shift.personName && (
              <span className="inline-flex items-center gap-1">
                <User size={14} />
                {shift.personName}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <MapPin size={14} />
              {shift.location}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {variant === "upcoming" ? (
          canEdit ? (
            <Button variant="outline" onClick={() => onEdit?.(shift.id)} className="gap-1">
              <Pencil size={16} /> Edit
            </Button>
          ) : (
            <Button variant="outline" onClick={() => onView?.(shift.id)} className="gap-1">
              <Eye size={16} /> View
            </Button>
          )
        ) : (
          <Button variant="outline" onClick={() => onView?.(shift.id)} className="gap-1">
            <Eye size={16} /> View
          </Button>
        )}
      </div>
    </div>
  )
}

// Everything that differs between the two sections other than the blocks themselves.
const TONES = {
  upcoming: {
    title: "Upcoming Blocks",
    emptyLabel: "No upcoming blocks",
    emptyStyle: "text-emerald-700 border-emerald-200 bg-emerald-50",
  },
  past: {
    title: "Past Blocks",
    emptyLabel: "No past blocks",
    emptyStyle:
      "text-palette-jungleGreen700 border-palette-jungleGreen200 bg-palette-jungleGreen000",
  },
} as const

// Generic over the block type so a caller handing in a richer Shift gets that same type back.
export function BlockSection<T extends Shift>({
  tone,
  shifts,
  showCount = true,
  canEdit = true,
  showPerson = false,
  onEdit,
  onView,
}: {
  tone: "upcoming" | "past"
  shifts: T[]
  // Off where the list is capped, so the badge cannot read as a total.
  showCount?: boolean
  canEdit?: boolean
  // Names the driver on each row, where the list is not already grouped by person.
  showPerson?: boolean
  onEdit?: (shift: T) => void
  onView?: (shift: T) => void
}) {
  const { title, emptyLabel, emptyStyle } = TONES[tone]

  return (
    <div>
      <SectionHeader title={title} count={showCount ? shifts.length : undefined} tone={tone} />
      {shifts.length === 0 ? (
        <div className={`text-sm border rounded-md p-4 ${emptyStyle}`}>{emptyLabel}</div>
      ) : (
        <div className="space-y-2">
          {shifts.map((shift) => (
            <ShiftRow
              key={shift.id}
              shift={toShiftItem(
                shift,
                showPerson ? (shift.people?.person ?? "Unassigned") : undefined,
              )}
              variant={tone}
              onEdit={() => onEdit?.(shift)}
              onView={() => onView?.(shift)}
              canEdit={canEdit}
            />
          ))}
        </div>
      )}
    </div>
  )
}
